// Session Storage Utilities
// Stores signed session tokens (HMAC) issued by verify-campaign-password.
// The token is opaque to the client — validation happens server-side via
// the verify-session-token edge function.

import { supabase } from "@/integrations/supabase/client";

const SESSION_EXPIRY_DAYS = 10;

export interface SessionData {
  email: string;
  expires_at: string;
}

export const getStorage = (rememberMe: boolean): Storage => {
  return rememberMe ? localStorage : sessionStorage;
};

/** Read the raw signed token string. */
const readToken = (key: string): { token: string; fromLocal: boolean } | null => {
  let token = sessionStorage.getItem(key);
  if (token) return { token, fromLocal: false };
  token = localStorage.getItem(key);
  if (token) return { token, fromLocal: true };
  return null;
};

/** Verify session server-side (HMAC signature + expiry). */
export const verifySessionRemote = async <T extends SessionData>(
  key: string
): Promise<(T & { remember_me?: boolean }) | null> => {
  const entry = readToken(key);
  if (!entry) return null;
  try {
    const { data, error } = await supabase.functions.invoke('verify-session-token', {
      body: { session_token: entry.token },
    });
    if (error || !data?.valid) {
      clearSession(key);
      return null;
    }
    return { ...(data.session as T), remember_me: entry.fromLocal };
  } catch {
    return null;
  }
};

/** Save a server-issued signed token. */
export const saveSessionToken = (key: string, token: string, rememberMe: boolean): void => {
  const storage = getStorage(rememberMe);
  storage.setItem(key, token);
  if (rememberMe) sessionStorage.removeItem(key);
  else localStorage.removeItem(key);
};

export const clearSession = (key: string): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

/** Preserved default expiry constant for callers that need it. */
export { SESSION_EXPIRY_DAYS };
